<script setup>
import { computed } from 'vue';
import { formatDate } from 'dashboard/helper/dateHelper.js';
import Icon from 'next/icon/Icon.vue';
import MetadataBadge from './MetadataBadge.vue';
import CustomClassificationBadge from './CustomClassificationBadge.vue';

const {
  reply,
  uiFlags,
  hideComment,
  deleteComment,
  parentModerationBusy,
  postSourceType,
  parentHidden,
  showDmAction,
} =
  defineProps({
    reply: {
      type: Object,
      required: true,
    },
    uiFlags: {
      type: Object,
      required: true,
    },
    hideComment: {
      type: Function,
      required: true,
    },
    deleteComment: {
      type: Function,
      required: true,
    },
  parentModerationBusy: {
    type: Boolean,
    default: false,
  },
  postSourceType: {
    type: String,
    default: '',
  },
  parentHidden: {
    type: Boolean,
    default: false,
  },
    showDmAction: {
      type: Boolean,
      default: false,
    },
  });

const emit = defineEmits(['openReplyPopup', 'openDmPopup']);

const showMetadataBadge = computed(() => {
  return !!reply.from_name && !!reply?.metadata?.purchase_intention;
});

const isMentionedComment = computed(() => {
  return reply.metadata?.type === 'MENTION';
});

const allowReplyWhileHidden = computed(() => {
  const st = postSourceType || (reply.post && reply.post.source_type) || '';
  return st === 'INSTAGRAM';
});

const isPrivateReply = computed(() => {
  return reply?.metadata?.delivery === 'PRIVATE_REPLY';
});

const showDmUnderHiddenParent = computed(() => {
  return (
    (postSourceType || '').toUpperCase() === 'INSTAGRAM' &&
    parentHidden &&
    !isMentionedComment.value
  );
});
</script>

<template>
  <div
    class="flex items-start gap-3 sm:gap-4 w-full py-3 px-3 sm:px-4 rounded-lg bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 transition-all hover:shadow-sm"
    :class="{
      'opacity-60': reply.hidden,
    }"
  >
    <!-- Avatar -->
    <div
      class="flex-shrink-0 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 shadow-sm h-8 w-8 sm:h-10 sm:w-10 flex justify-center items-center"
    >
      <Icon icon="i-lucide-user" class="size-4 sm:size-5 text-white" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0 space-y-2">
      <!-- User Info & Badges -->
      <div class="flex flex-col sm:flex-row sm:items-center gap-2">
        <div class="flex items-center gap-2">
          <h4 class="text-sm font-semibold text-slate-800 dark:text-slate-200">
            {{ reply.from_name || reply.inbox?.name }}
          </h4>
          <span
            v-if="reply.user"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
            :title="
              $t('COMMENTS.REPLY.RESPONDED_BY_USER', { user: reply.user.name })
            "
          >
            <Icon icon="i-lucide-user-check" class="size-3" />
            {{ reply.user.name }}
          </span>
          <span
            v-if="reply.classification === 'AI_REPLY'"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
          >
            <Icon icon="i-lucide-sparkles" class="size-3" />
            {{ $t('COMMENTS.REPLY.AI_LABEL') }}
          </span>
          <span
            v-if="isPrivateReply"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200"
            :title="$t('COMMENTS.REPLY.SENT_AS_DM')"
          >
            <Icon icon="i-lucide-message-circle" class="size-3" />
            {{ $t('COMMENTS.REPLY.SENT_AS_DM') }}
          </span>
          <span
            v-if="reply.hidden"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
          >
            <Icon icon="i-lucide-eye-off" class="size-3" />
            {{ $t('COMMENTS.REPLY.HIDDEN') }}
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <MetadataBadge v-if="showMetadataBadge" :type="reply.metadata" />
          <CustomClassificationBadge
            v-if="
              Array.isArray(reply.metadata?.custom_classifications) &&
              reply.metadata.custom_classifications.length
            "
            :items="reply.metadata.custom_classifications"
            :max-visible="2"
          />
        </div>
      </div>

      <!-- Message -->
      <div
        v-if="!reply.hidden || allowReplyWhileHidden"
        class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700/50"
      >
        <p
          class="text-sm text-slate-700 dark:text-slate-300 break-words whitespace-pre-wrap leading-relaxed"
        >
          {{ reply.message }}
        </p>
      </div>

      <!-- Timestamp & Actions -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1"
      >
        <div
          class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
        >
          <Icon icon="i-lucide-clock" class="size-3.5" />
          <span>{{ formatDate(reply.created_time) }}</span>
        </div>

        <div class="flex items-center gap-2">
          <button
            v-if="showDmUnderHiddenParent"
            type="button"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-woot-600 dark:text-woot-400 hover:text-woot-700 dark:hover:text-woot-300 hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
            :disabled="uiFlags.isFetching || parentModerationBusy"
            @click.stop.prevent="
              emit('openDmPopup', {
                commentId: reply.id,
                authorName: reply.from_name || reply.inbox?.name || '',
              })
            "
          >
            <Icon icon="i-lucide-message-circle" class="size-3.5" />
            {{ $t('COMMENTS.CARD.SEND_DM') }}
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-woot-600 dark:text-woot-400 hover:text-woot-700 dark:hover:text-woot-300 hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
            :disabled="uiFlags.isFetching || parentModerationBusy"
            @click.stop.prevent="
              emit('openReplyPopup', {
                replyId: reply.id,
                authorName: reply.from_name || reply.inbox?.name || '',
              })
            "
          >
            <Icon icon="i-lucide-reply" class="size-3.5" />
            {{ $t('COMMENTS.CARD.REPLY') }}
          </button>
          <button
            v-if="showDmAction && !reply.hidden"
            type="button"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
            :disabled="uiFlags.isFetching || parentModerationBusy"
            @click.stop="
              emit('openDmPopup', {
                replyId: reply.id,
                authorName: reply.from_name || reply.inbox?.name || '',
              })
            "
          >
            <Icon icon="i-lucide-mail" class="size-3.5" />
            {{ $t('COMMENTS.MODERATION_CARD.SEND_DM') }}
          </button>
          <button
            v-if="!isMentionedComment"
            type="button"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
            :disabled="uiFlags.isFetching || parentModerationBusy"
            @click.stop.prevent="deleteComment(reply.id)"
          >
            <Icon icon="i-lucide-trash-2" class="size-3.5" />
            {{ $t('COMMENTS.REPLY.DELETE') }}
          </button>
          <button
            v-if="!isMentionedComment"
            type="button"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-woot-600 dark:hover:text-woot-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
            :disabled="uiFlags.isFetching || parentModerationBusy"
            @click.stop.prevent="hideComment(reply.id)"
          >
            <Icon
              :icon="reply.hidden ? 'i-lucide-eye' : 'i-lucide-eye-off'"
              class="size-3.5"
            />
            {{
              reply.hidden
                ? $t('COMMENTS.REPLY.SHOW')
                : $t('COMMENTS.REPLY.HIDE')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
